import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, Building2, TrendingUp, Warehouse, Code2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn, MaskedLine, Marquee, SpotlightCard } from '../components/ui-bits';

const Home = () => {
  const { t, lang } = useLanguage();

  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const bgY = useTransform(heroProgress, [0, 1], ['0%', '30%']);
  const bgScale = useTransform(heroProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(heroProgress, [0, 1], [0, -60]);
  const beamOpacity = useTransform(heroProgress, [0, 0.6], [1, 0.2]);

  const verticals = [
    { key: 'v1', icon: Building2, to: '/que-hacemos/gestion-inmobiliaria', num: '01' },
    { key: 'v2', icon: TrendingUp, to: '/que-hacemos/inversion-inmobiliaria', num: '02' },
    { key: 'v3', icon: Warehouse, to: '/que-hacemos/bodegaje-industrial', num: '03' },
    { key: 'v4', icon: Code2, to: '/que-hacemos/desarrollo-software', num: '04' },
  ];

  const marqueeItems = lang === 'en'
    ? ['Bringing light', 'Since 2012', 'Chile', 'Four verticals', 'Inversiones Tair SpA', 'Since 2012']
    : ['Va a traer luz', 'Desde 2012', 'Chile', 'Cuatro verticales', 'Inversiones Tair SpA', 'Desde 2012'];

  return (
    <div data-testid="home-page">
      {/* ============ HERO — KINETIC / MASKED REVEAL ============ */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] flex items-end overflow-hidden pb-20 md:pb-28"
        data-testid="hero-section"
      >
        {/* Parallax background image */}
        <motion.div
          style={{ y: bgY, scale: bgScale }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url('https://delabase.cl/wp-content/uploads/2026/01/volcan-fachada.jpg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.34,
            }}
          />
        </motion.div>

        {/* Gradient wash */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#070d18]/40 via-[#070d18]/70 to-[#070d18]" />

        {/* Spotlight beam */}
        <motion.div style={{ opacity: beamOpacity }} className="absolute inset-0 z-[2] spotlight-hero pointer-events-none" />

        {/* Rotating flare — subtle */}
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] z-[2] pointer-events-none opacity-40">
          <div className="flare w-full h-full rounded-full"
               style={{
                 background: 'conic-gradient(from 0deg, transparent, rgba(232,176,78,0.15), transparent, rgba(232,176,78,0.08), transparent)',
                 filter: 'blur(60px)',
               }}
          />
        </div>

        {/* Top-right meta */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute top-28 md:top-32 right-6 md:right-10 lg:right-12 z-10 hidden md:flex flex-col items-end gap-1 edge-number"
        >
          <span>N° 001 — Holding</span>
          <span>Santiago, Chile</span>
          <span>Est. 2012</span>
        </motion.div>

        {/* Content */}
        <motion.div
          style={{ y: contentY }}
          className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-12"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.6 }}
          >
            <Eyebrow>{t.home.eyebrow}</Eyebrow>
          </motion.div>

          <h1
            className="mt-10 md:mt-14 font-display font-extrabold tracking-tight text-white leading-[0.92]"
            style={{ fontSize: 'clamp(4rem, 13vw, 12rem)' }}
            data-testid="hero-title"
          >
            <MaskedLine delay={0.12}>
              <span className="text-white">{t.home.heroTitle1}</span>
            </MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="font-serif-i text-amber-300 text-glow" style={{ fontWeight: 400 }}>
                {t.home.heroTitle2}
              </span>
            </MaskedLine>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-6 font-body text-base md:text-lg text-white/65 leading-relaxed max-w-xl"
            >
              {t.home.heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-6 flex flex-wrap gap-3 lg:justify-end"
            >
              <Link
                to="/que-hacemos"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-amber-300 text-[#0a1322] font-semibold text-sm hover:bg-amber-200 transition-colors"
              >
                <span>{t.home.ctaPrimary}</span>
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </Link>
              <Link
                to="/quienes-somos"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-transparent border border-white/20 text-white font-semibold text-sm hover:bg-white/5 hover:border-white/40 transition-colors"
              >
                {t.home.ctaSecondary}
              </Link>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="hidden md:flex mt-20 items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-white/45"
          >
            <span className="w-8 h-px bg-white/30" />
            <span>{lang === 'en' ? 'Scroll to unveil' : 'Desliza para descubrir'}</span>
          </motion.div>
        </motion.div>
      </section>

      {/* ============ MARQUEE — EDITORIAL SLOW TICKER ============ */}
      <section className="relative py-12 md:py-16 border-y border-white/[0.06] bg-[#050912]" data-testid="hero-marquee">
        <Marquee items={marqueeItems} />
      </section>

      {/* ============ MEANING / MANIFESTO ============ */}
      <Section className="py-28 md:py-40" dataTestId="brand-story-section" edgeLabel="§ 01 — Meaning">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <FadeIn className="lg:col-span-5">
            <Eyebrow>{t.home.meaning}</Eyebrow>
            <div className="mt-6 inline-flex items-baseline gap-3 text-white/45 font-mono text-xs uppercase tracking-widest">
              <span>{t.home.meaningDef}</span>
              <span className="text-white/25">·</span>
              <span>{t.home.meaningPhonetic}</span>
            </div>
            <h2 className="mt-8 font-display font-extrabold tracking-tight text-4xl md:text-5xl lg:text-6xl text-white leading-[1.02]">
              {lang === 'en' ? 'Will bring' : 'Va a traer'}
              <br />
              <span className="font-serif-i text-amber-300" style={{ fontWeight: 400 }}>
                {lang === 'en' ? 'light.' : 'luz.'}
              </span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-6 lg:col-start-7 lg:pt-10">
            <p className="font-body text-lg md:text-xl text-white/70 leading-relaxed">
              {t.home.meaningCopy}
            </p>
            <div className="mt-12 h-px w-full shimmer-line bg-white/5" />
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { l: t.home.stat1Label, v: t.home.stat1Value },
                { l: t.home.stat2Label, v: t.home.stat2Value },
                { l: t.home.stat3Label, v: t.home.stat3Value },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-display text-3xl md:text-4xl font-bold text-amber-300">{s.v}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/45 font-mono">{s.l}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* ============ VERTICALS — NUMBERED MANIFESTO CHAPTERS ============ */}
      <Section className="pb-32 md:pb-44" dataTestId="home-verticals-section" edgeLabel="§ 02 — Verticals">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16 md:mb-24">
          <FadeIn className="lg:col-span-6">
            <Eyebrow>{lang === 'en' ? '04 — What We Do' : '04 — Qué Hacemos'}</Eyebrow>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
              {lang === 'en' ? 'Four fronts.' : 'Cuatro frentes.'}
              <br />
              <span className="font-serif-i text-amber-300" style={{ fontWeight: 400 }}>
                {lang === 'en' ? 'One principle.' : 'Un mismo principio.'}
              </span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed">
              {t.home.verticalsSubtitle}
            </p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {verticals.map((v, i) => {
            const Icon = v.icon;
            const data = t.verticals[v.key];
            return (
              <FadeIn key={v.key} delay={i * 0.08}>
                <SpotlightCard className="block h-full p-8 md:p-10">
                  <Link
                    to={v.to}
                    data-testid={`home-vertical-${v.key}`}
                    className="group block h-full"
                  >
                    <div className="flex items-start justify-between mb-14">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-amber-300/10 border border-amber-300/25 flex items-center justify-center text-amber-300">
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="font-mono text-sm text-white/45 tracking-widest">{v.num} / 04</span>
                      </div>
                      <div className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/40 group-hover:border-amber-300 group-hover:text-amber-300 group-hover:rotate-45 transition-all duration-500">
                        <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                    <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white tracking-tight">{data.title}</h3>
                    <p className="mt-4 font-body text-base text-white/55 leading-relaxed">{data.short}</p>
                    <div className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-300/70 font-mono">
                      <span className="w-6 h-px bg-amber-300/50" />
                      {lang === 'en' ? 'Read chapter' : 'Leer capítulo'}
                    </div>
                  </Link>
                </SpotlightCard>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* ============ EDITORIAL CLOSING QUOTE ============ */}
      <Section className="pb-32 md:pb-44" dataTestId="editorial-closing" edgeLabel="§ 03 — Signature">
        <FadeIn>
          <div className="border-t border-white/[0.08] pt-16 md:pt-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-2 flex items-start">
                <span className="manifesto-num text-sm">— 2012 / ∞</span>
              </div>
              <div className="lg:col-span-9">
                <p className="font-serif-i text-white/90" style={{ fontSize: 'clamp(2rem, 5vw, 4.5rem)', lineHeight: '1.05' }}>
                  {lang === 'en'
                    ? 'We invest where we can sustain projects for decades, not quarters.'
                    : 'Invertimos donde podemos sostener proyectos por décadas, no por trimestres.'}
                </p>
                <div className="mt-10 flex items-center gap-4 text-white/45 text-xs font-mono uppercase tracking-widest">
                  <span className="w-8 h-px bg-amber-300/60" />
                  <span>Inversiones Tair SpA</span>
                  <span className="star-divider" />
                  <span>Chile</span>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
};

export default Home;
